import { widgetType, GroupingValue, Months } from "src/app/enums";
import { gridItem } from "src/app/models";
import { WidgetBaseComponent } from "../widgets/widget-base.component";
import * as moment from "moment";

type WidgetComponent = new () => WidgetBaseComponent;

const WIDGETS = new Map<string, WidgetComponent>();

export function getTypeFor(name: string): WidgetComponent {
    return WIDGETS.get(name);
}

export function register(key: string): any {
    return (cls) => {
        WIDGETS.set(key, cls);
    };
};

export function deepCheckObject(object1, object2): boolean {
    if (object1 == null && object2 == null) return true;
    if (object1 != null && object2 == null || object1 == null && object2 != null) return false;

    const keys1 = Object.keys(object1);
    const keys2 = Object.keys(object2);
    if (keys1.length !== keys2.length) {
        return false;
    }
    for (const key of keys1) {
        const val1 = object1[key];
        const val2 = object2[key];
        const areObjects = isObject(val1) && isObject(val2);
        if (
            areObjects && !deepCheckObject(val1, val2) ||
            !areObjects && val1 !== val2
        ) {
            return false;
        }
    }
    return true;
}

export function canculateGridItemSize(item: gridItem, arrayMaxSize: number) {
    switch (item.type) {
        case widgetType.Table:
            item.cols = 12;
            item.rows = 6;
            break;
        case widgetType.Label:
            item.cols = Math.min(24, arrayMaxSize * 3 + 1);
            item.rows = 4;
            break;
        case widgetType.Areachart:
        case widgetType.Barchart:
        case widgetType.Linechart:
            item.cols = 6;
            item.rows = 6;
            break;
    }
}

export const TimeObject = {
    "Queue": {
        "WaitTimeMax": function (ms) { return convertSecondsToTime(ms,true) },
        "WaitTimeAverage": function (ms) { return convertSecondsToTime(ms,true) },
        "AvgSpeaktime": function (s) { return convertSecondsToTime(s,true) },
        "AvgSpeakTimeSec": function (s) { return convertSecondsToTime(s,true) },
        "AvgWrapup": function (s) { return convertSecondsToTime(s,true) },
        "AvgWaitTimeAnsweredSec": function (s) { return convertSecondsToTime(s,true) },
        "AHT": function (s) { return convertSecondsToTime(s,true) }
    },
    "Agent": {
        "SecondsInPause": function (ms) { return convertSecondsToTime(ms,true) },
        "TimeInStatus": function (ms) { return convertSecondsToTime(ms,true) },
        "TimeLoggedOn": function (s) { return convertSecondsToTime(s,true) },
        "SecondsLoggedIn": function (s) { return convertSecondsToTime(s,true) },
        "AvgSpeaktime": function (s) { return convertSecondsToTime(s,true) },
        "AvgWrapup": function (s) { return convertSecondsToTime(s,true) },
        "AHT": function (s) { return convertSecondsToTime(s,true) },
        "TotalSpeaktimeSec": function (s) { return convertSecondsToTime(s,true) }
    }
}

export const PercentageFields = ["AbandonedRate", "AnswerRate", "AnswerRateSLAexAbandon", "AnswerRateSLAexAbandonSLA", "AnswerRateWithinSla", "SilentRate"];

function convertSecondsToTime(totalSeconds,showDays = false) {
    if (!totalSeconds) {
        return "00:00:00";
    }
    const days = Math.floor(totalSeconds / (3600 * 24));
    const hours = Math.floor((totalSeconds / 3600) % 24);
    const minutes = Math.floor(totalSeconds % 3600 / 60);
    const seconds = totalSeconds % 60;
    if (!showDays) {
      return padTo2(hours + 24 * days) + ':' + padTo2(minutes) + ':' + padTo2(seconds);
    }
    if (days > 0) {
        return padTo2(days) + 'd ' + padTo2(hours) + ':' + padTo2(minutes) + ':' + padTo2(seconds);
    }

    return padTo2(hours) + ':' + padTo2(minutes) + ':' + padTo2(seconds);
}

export function dateTimeToDate(value: string): string {
    const valueTokens: string[] = value.split("T")[0].split("-");
    const year = valueTokens[0];
    const month = valueTokens[1];
    const day = valueTokens[2];

    return `${day}/${month}/${year}`
}

export function dateTimeToTime(value: string): string {
    return value.split("T")[1];
}

export function secondsToTime(value, toFloor = false, showDays = false) {
    value = Math.round(value);
    if (!value) {
        return '00:00:00';
    }
    const days = Math.floor(+value / (3600 * 24));
    const hours = Math.floor((+value / 3600) % 24);
    const minutes = Math.floor(+value % 3600 / 60);
    const seconds =(toFloor?-Math.round(-value): -Math.round(-value)) % 60;
    if (!showDays) {
      return padTo2(hours + 24 * days) + ':' + padTo2(minutes) + ':' + padTo2(seconds);
    }
    if (days > 0) {
        return `${padTo2(days)}d ${padTo2(hours)}:${padTo2(minutes)}:${padTo2(seconds)}`;
    }
    return `${padTo2(hours)}:${padTo2(minutes)}:${padTo2(seconds)}`;
}

export function percentages(value,digits){
    return new Intl.NumberFormat('default', {
        style: 'percent',
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      }).format((value||0)*100 / 100);
}

export function periodIsDayOrMonth(fromDate: Date, toDate: Date) {
  if (fromDate == null || toDate == null) return false;
  let from = moment(fromDate).utc();
  let to = moment(toDate).utc();
  let diffInSeconds = moment.duration(to.diff(from)).asSeconds();
  if ((diffInSeconds == 24 * 60 * 60 - 1 || diffInSeconds == 24 * 60 * 60) && from.hour() == 0 && from.minutes() == 0) return true;
  if (from.isSame(to, 'month') && (from.date() == 1) && (from.hour() == 0) && (from.minutes() == 0) && (to.daysInMonth() == to.date())) return true;
  if (moment.duration(to.diff(from)).asDays() == from.daysInMonth() && (from.date() == 1) && (from.hour() == 0) && (from.minutes() == 0) ) return true;
  return false;
}


export function round(value, places: number) {
  return Math.round((value || 0) * Math.pow(10, places)) / Math.pow(10, places);
}

function padTo2(value) {
    if (!value) {
        return '00';
    }
    return value < 10 ? String(value).padStart(2, '0') : value;
}

function isObject(object): boolean {
    return object != null && typeof object === 'object';
}

export function pad(num, size) {
    if (num == null) return null;
    num = num.toString();
    while (num.length < size) num = "0" + num;
    return num;
}

const canvas = document.createElement("canvas");
export function calculateTextSize(text: string, font: string): number {
    const context = canvas.getContext("2d");
    context.font = font;
    const metrics = context.measureText(text);
    return metrics.width;
}

export function getDateTime(stringToParse: string) {
  const [date, time] = (stringToParse || "1970-01-01T00:00:00").split("T");
  const [year, month, day] = date.split('-');
  return {year, month, day, time, dateString: date, timeString: time};
}

export function getMonthName(month: Months) {
  return {
    "01": "January",
    "02": "February",
    "03": "March",
    "04": "April",
    "05": "May",
    "06": "June",
    "07": "July",
    "08": "August",
    "09": "September",
    "10": "October",
    "11": "November",
    "12": "December"
  }[month];
}

export function getGroupingValue(value: GroupingValue, from: string, to: string) {
  const { year: fromYear, month: fromMonth, day: fromDay, time: fromTime, dateString: fromDateString } = getDateTime(from);
  const { dateString: toDateString } = getDateTime(to);

  const groupingValueMap: Record<GroupingValue, string> = {
    "M": `${getMonthName(<Months>fromMonth)} ${fromYear}`,
    "D": `${fromDay}/${fromMonth}/${fromYear}`,
    "H": `${fromTime}<br/>${fromDateString}`,
    "Q": `${fromTime}<br/>${fromDateString}`,
    "W": `${fromDateString}<br/>${toDateString}`
  };

  return groupingValueMap[value]
}

export const responsiveChart = {
  height200: {
      condition: {
          maxHeight: 200
      },
      chartOptions: {
          legend: {
            enabled: false
          },
          navigator: {
            height: 0
          },
          xAxis: {
              title: {
                style: {
                  display: 'none'
                }
              },
              labels: {
                  enabled: false
              }
          },
          yAxis: {
            title: {
              style: {
                display: 'none'
              }
            }
          }
      }
    },
    height300: {
      condition: {
          maxHeight: 300
      },
      chartOptions: {
          legend: {
            enabled: false
          },
          xAxis: {
            labels: {
                enabled: false
            }
          }
      }
    },
    height350: {
      condition: {
          maxHeight: 350
      },
      chartOptions: {
          xAxis: {
            labels: {
                enabled: false
            }
          }
      }
    }
}
